import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  SelectPicker,
  TagPicker,
  Button,
  Stack,
  IconButton,
  Message,
  useToaster,
  List
} from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { RiUserLocationLine } from 'react-icons/ri';
import { addIndividualYAccess } from '@/services/access.service';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';
import { useAppDispatch, useAppSelector } from '@/config/hooks';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const AddAccessDrawer = ({ open, onClose, users, withPriority }) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const [loading, setLoading] = useState(false);

  const floors = useAppSelector(state => state.floor?.data || [])?.map(item => ({
    value: item._id,
    label: item.name
  }));

  const zones = useAppSelector(state => state.zone?.data || [])?.map(item => ({
    value: item._id,
    label: item.name,
    floor: item.floor
  }));

  useEffect(() => {
    dispatch(getFloors());
    dispatch(getZones());
  }, [dispatch]);

  const [formValue, setFormValue] = useState({
    User: ''
  });

  const [localAccessList, setLocalAccessList] = useState([
    {
      floor: null,
      zones: []
    }
  ]);

  const [selectedZones, setSelectedZones] = useState<any>([]);

  const handleSubmit = async () => {
    if (!formValue.User || selectedZones.length === 0) {
      toaster.push(<Message type="error">Please select a user and at least one zone</Message>, {
        placement: 'topCenter'
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        User: formValue.User,
        Zones: selectedZones
      };

      console.log('Submitting payload:', payload);
      console.log('Selected Zones:', selectedZones);

      const response = await dispatch(addIndividualYAccess(payload));
      console.log('API Response:', response);

      toaster.push(<Message type="success">{t('Individual access added successfully')}</Message>, {
        placement: 'topCenter'
      });

      setFormValue({ User: '' });
      setLocalAccessList([{ floor: null, zones: [] }]);
      setSelectedZones([]);
      onClose();
    } catch (error) {
      console.error('Error adding individual access:', error);
      toaster.push(
        <Message type="error">
          {t(error?.response?.data?.message) || t(error?.message) || t('Error adding individual access')}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        floor: null,
        zones: []
      }
    ]);
  };

  const handleRemoveRow = index => {
    const values = [...localAccessList];
    const floorId = values[index]?.floor;
    const floorZones = zones.filter(zone => zone.floor === floorId);
    const newZones = selectedZones.filter(zoneId => !floorZones.some(z => z._id === zoneId));
    setSelectedZones(newZones);
    values.splice(index, 1);
    setLocalAccessList(values);
  };

  const handleChangeFloor = (index, floorId) => {
    const newArr = [...localAccessList];
    newArr[index].floor = floorId;
    newArr[index].zones = [];
    setLocalAccessList(newArr);
  };

  if (withPriority) {
    return null;
  }
  const { t } = useTranslation();
  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Add Individual Access')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary" loading={loading}>
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User')}</Form.ControlLabel>
            <SelectPicker
              block
              data={users.map(user => ({
                label: `${user.firstname} ${user.lastname}`,
                value: user._id
              }))}
              value={formValue.User}
              onChange={value => setFormValue(prev => ({ ...prev, User: value }))}
              searchable={true}
              cleanable={false}
              placeholder={t("Select User")}
            />
          </Form.Group>

          <Stack style={{ marginTop: 50, marginBottom: 10, marginLeft: 10 }} spacing={5}>
            <RiUserLocationLine />
            <h6 className="title">{t('Manage Zones')}</h6>
          </Stack>

          <div className="ml-10">
            <List hover>
              {localAccessList.map((item, index) => (
                <List.Item key={index} index={index + 1}>
                  <Stack justifyContent="space-around" wrap>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Floor')}</div>
                      <SelectPicker
                        value={item.floor}
                        onChange={value => handleChangeFloor(index, value)}
                        data={floors}
                        placement="auto"
                        style={{ width: 250 }}
                        searchable={false}
                        cleanable={false}
                        placeholder={t("Select Floor")}
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== index)
                          .map(item => item.floor)
                          .filter(Boolean)}
                      />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Zones')}</div>
                      <TagPicker
                        data={zones.filter(zone => zone.floor === item.floor)}
                        value={selectedZones}
                        onChange={setSelectedZones}
                        style={{ width: 250 }}
                        placement="auto"
                        placeholder={t("Select Zones")}
                        disabled={!item.floor}
                      />
                    </div>
                    {localAccessList.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveRow(index)}
                        icon={<MinusIcon />}
                        size="xs"
                      />
                    )}
                  </Stack>
                </List.Item>
              ))}
            </List>
            <IconButton
              onClick={handleAddRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default AddAccessDrawer;
